/**
 * ClauseNegotiatorDialog — Négociateur IA : quand un risque est détecté,
 * l'IA propose une clause améliorée + un email de contre-proposition + le
 * fondement juridique.
 */
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Sparkles, Loader2, Copy, Scale, Mail, FileText, AlertTriangle,
} from "lucide-react";import { toast } from "sonner";

export default function ClauseNegotiatorDialog({ open, onOpenChange, contract, risk }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [extra, setExtra] = useState("");

  const negotiate = async () => {
    if (!risk) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/negotiate`, {
        risk_code: risk.code, user_message: extra,
      });
      setResult(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success("Copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const reset = () => { setResult(null); setExtra(""); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
                     data-testid="negotiator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Scale className="h-3.5 w-3.5 text-white" />
            </div>
            Négociateur IA
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          <div className="text-xs text-slate-500 mt-0.5">
            {risk?.explanation || "L'IA propose une clause améliorée + contre-proposition."}
          </div>
        </DialogHeader>

        {!result && (
          <div className="space-y-3 overflow-y-auto pr-2">
            {risk?.quote && (
              <Card className="p-3 bg-red-50/40 border-red-200">
                <div className="text-[11px] font-medium uppercase tracking-wide text-red-700 mb-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Clause d'origine problématique
                </div>
                <blockquote className="text-xs text-slate-700 italic border-l-2 border-red-300 pl-2">
                  « {risk.quote} »
                </blockquote>
              </Card>
            )}
            <div>
              <Label className="text-xs">Contexte additionnel (optionnel)</Label>
              <Textarea value={extra} onChange={(e) => setExtra(e.target.value)}
                        rows={3} className="mt-1 text-sm"
                        placeholder="Ex: nous avons déjà négocié cette clause l'an dernier, ou : contrat critique pour l'activité…"
                        data-testid="negotiator-context" />
            </div>
            <Button onClick={negotiate} disabled={busy}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="negotiator-run">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    : <Sparkles className="h-4 w-4 mr-1.5" />}
              Proposer une correction
            </Button>
          </div>
        )}

        {result && (
          <Tabs defaultValue="clause" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="clause" data-testid="tab-clause">
                <FileText className="h-3.5 w-3.5 mr-1.5" /> Clause améliorée
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="tab-email">
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
              </TabsTrigger>
              <TabsTrigger value="rationale" data-testid="tab-rationale">
                <Scale className="h-3.5 w-3.5 mr-1.5" /> Fondement
              </TabsTrigger>
            </TabsList>

            <TabsContent value="clause" className="flex-1 overflow-y-auto mt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-700">
                  Nouvelle clause à faire ajouter
                </div>
                <Button size="sm" variant="ghost" onClick={() => copy(result.improved_clause)}>
                  <Copy className="h-3 w-3 mr-1" /> Copier
                </Button>
              </div>
              <Card className="p-4 bg-emerald-50/30 border-emerald-200">
                <pre className="text-xs whitespace-pre-wrap font-sans text-slate-800 leading-relaxed">
                  {result.improved_clause}
                </pre>
              </Card>
              <div className="mt-3">
                <Button size="sm" onClick={async () => {
                  try {
                    const r = await api.post(`/contracts/${contract.id}/avenant/download`,
                      { improved_clause: result.improved_clause, risk_code: risk.code },
                      { responseType: "blob" });
                    const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `Avenant - ${contract.title || contract.party_name || "contrat"}.docx`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) { toast.error(apiError(e)); }
                }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="avenant-download-btn">
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> Télécharger l'avenant DOCX
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="email" className="flex-1 overflow-y-auto mt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-medium uppercase tracking-wide text-blue-700">
                  Email à envoyer à la contrepartie
                </div>
                <Button size="sm" variant="ghost" onClick={() => copy(result.counter_proposal_email)}>
                  <Copy className="h-3 w-3 mr-1" /> Copier
                </Button>
              </div>
              <Card className="p-4 bg-blue-50/30 border-blue-200">
                <pre className="text-xs whitespace-pre-wrap font-sans text-slate-800 leading-relaxed">
                  {result.counter_proposal_email}
                </pre>
              </Card>
            </TabsContent>

            <TabsContent value="rationale" className="flex-1 overflow-y-auto mt-3">
              <div className="text-[11px] font-medium uppercase tracking-wide text-purple-700 mb-2">
                Fondement juridique
              </div>
              <Card className="p-4 bg-purple-50/30 border-purple-200">
                <p className="text-sm text-slate-800 leading-relaxed">
                  {result.legal_rationale}
                </p>
              </Card>
            </TabsContent>

            <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
              <Button variant="outline" onClick={reset}>Régénérer</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
